import { useState, useCallback, useEffect } from "react";
import { createPortal } from "react-dom";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useUser, Show } from "@clerk/react";
import { useQuery } from "@tanstack/react-query";
import { AuthModal } from "@/components/ui/auth-modal";
import { useGenerate } from "@/hooks/use-generation";
import { useUserStore } from "@/stores/user-store";
import { CREDIT_COSTS, getModelOption, getGenerationCreditCost, INTEGRATED_MODEL_ID, INTEGRATED_DURATION_MIN, INTEGRATED_DURATION_MAX, type ModelId } from "@/config/models";
import { api, buildQueryString } from "@/lib/api";
import { extractLastFrameAsBase64 } from "@/lib/video-utils";
import { NsfwOrError } from "@/components/ui/nsfw-error";
import { IconCoin } from "@/components/ui/cartoon-icons";
import { UpsellModal } from "@/components/ui/upsell-modal";
import { cn } from "@/lib/utils";
import { isExpired } from "@/config/retention";
import {
  Loader2,
  ArrowRight,
  Lock,
  ArrowLeft,
  FastForward,
  Film,
} from "lucide-react";
import { trackViewContent } from "@/services/analytics";
import type { Video } from "@/types";

const TABS = [
  { href: "/create-cartoon", label: "Create" },
  { href: "/create-character", label: "Character" },
  { href: "/extend-cartoon", label: "Extend" },
];

export function ExtendCartoonPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const preselectVideoId = searchParams.get("video_id");
  const { user, isSignedIn } = useUser();
  const { credits, hasPurchased, isLoading: isUserLoading } = useUserStore();
  const generateMutation = useGenerate();

  const [selectedVideo, setSelectedVideo] = useState<Video | null>(null);
  const [lastFrame, setLastFrame] = useState<string | null>(null);
  const [extracting, setExtracting] = useState(false);
  const [extractError, setExtractError] = useState<string | null>(null);
  const [prompt, setPrompt] = useState("");
  const [showAuth, setShowAuth] = useState(false);
  const [showCreditsModal, setShowCreditsModal] = useState(false);
  const [fakeLoading, setFakeLoading] = useState(false);
  const [preselectApplied, setPreselectApplied] = useState(false);

  useEffect(() => {
    trackViewContent("Extend Cartoon", user?.id, { funnel_stage: "create" });
  }, []);

  const { data, isLoading: videosLoading } = useQuery({
    queryKey: ["videos", user?.id],
    queryFn: async () => {
      if (!user?.id) throw new Error("No user");
      return api.get<{ success: boolean; videos: Video[] }>(
        "/videos",
      );
    },
    enabled: !!user?.id && hasPurchased,
  });

  // Expired videos have no file left to take the last frame from.
  const completedVideos = (data?.videos ?? []).filter(
    (v) => v.status === "completed" && v.media_type === "VIDEO" && v.url && !isExpired(v.created_at),
  );

  const handleSelectVideo = useCallback(async (video: Video) => {
    setSelectedVideo(video);
    setExtractError(null);
    setExtracting(true);
    try {
      const frame = await extractLastFrameAsBase64(video.url!);
      setLastFrame(frame);
    } catch {
      setExtractError("Failed to extract the last frame. Please try another video.");
      setSelectedVideo(null);
    } finally {
      setExtracting(false);
    }
  }, []);

  // Auto-select a preselected video from the ?video_id= param (deep-link from
  // the gen success page). Runs once when the videos list is available.
  useEffect(() => {
    if (preselectApplied || !preselectVideoId || !hasPurchased) return;
    const match = completedVideos.find((v) => v.id === preselectVideoId);
    if (match) {
      setPreselectApplied(true);
      handleSelectVideo(match);
      setSearchParams({}, { replace: true });
    }
  }, [
    preselectVideoId,
    preselectApplied,
    hasPurchased,
    completedVideos,
    handleSelectVideo,
    setSearchParams,
  ]);

  const handleBack = useCallback(() => {
    setSelectedVideo(null);
    setLastFrame(null);
    setPrompt("");
    setExtractError(null);
  }, []);

  const extendSeconds =
    selectedVideo?.model_id === INTEGRATED_MODEL_ID
      ? Math.min(
          INTEGRATED_DURATION_MAX,
          Math.max(
            INTEGRATED_DURATION_MIN,
            (selectedVideo.segment_duration_ms ?? selectedVideo.duration_ms)
              ? Math.round(
                  (selectedVideo.segment_duration_ms ?? selectedVideo.duration_ms)! / 1000,
                )
              : INTEGRATED_DURATION_MIN,
          ),
        )
      : undefined;

  const selectedModelOption = selectedVideo
    ? getModelOption(selectedVideo.model_id as ModelId)
    : undefined;
  const creditCost = selectedModelOption
    ? getGenerationCreditCost(selectedModelOption, extendSeconds)
    : selectedVideo
      ? (CREDIT_COSTS[selectedVideo.model_id as ModelId] ?? selectedVideo.credits_used)
      : 0;

  const canGenerate = prompt.trim().length > 0 && lastFrame && selectedVideo;

  const handleGenerate = useCallback(async () => {
    if (!canGenerate || !selectedVideo || !lastFrame || isUserLoading) return;

    if (credits < creditCost) {
      setShowCreditsModal(true);
      return;
    }

    try {
      const params: Record<string, unknown> = {
        model_id: selectedVideo.model_id,
        prompt: prompt.trim(),
        source_video_id: selectedVideo.id,
      };

      if (selectedVideo.model_id === INTEGRATED_MODEL_ID && extendSeconds) {
        params.duration_seconds = extendSeconds;
      }

      const result = await generateMutation.mutateAsync(params as any);
      if (result.video?.id) {
        navigate(`/generation/${result.video.id}`);
      }
    } catch (err) {
      console.error("Extend generation failed:", err);
    }
  }, [canGenerate, selectedVideo, lastFrame, prompt, credits, creditCost, extendSeconds, generateMutation, navigate, isUserLoading]);

  const renderExtendButtons = () => (
    <>
      <Show when="signed-out">
        <div>
          <button
            onClick={() => {
              setFakeLoading(true);
              setTimeout(() => {
                setFakeLoading(false);
                setShowAuth(true);
              }, 2000);
            }}
            disabled={fakeLoading}
            className="btn-accent w-full py-3.5 text-[15px] flex items-center justify-center gap-2"
          >
            {fakeLoading ? (
              <><Loader2 className="size-4 animate-spin" />Extending...</>
            ) : (
              <>Extend Video <ArrowRight className="size-4" /></>
            )}
          </button>
          <p className="flex items-center justify-center gap-1 text-[10px] text-[#241d3a]/45 font-bold mt-1.5">
            <IconCoin className="size-2.5" />{creditCost} tokens
          </p>
        </div>
      </Show>

      <Show when="signed-in">
        <div>
          {isUserLoading ? (
            <div className="w-full h-[50px] rounded-2xl bg-[#b8a5f0]/25 animate-pulse" />
          ) : (
            <button
              onClick={handleGenerate}
              disabled={!canGenerate || generateMutation.isPending}
              className="btn-accent w-full py-3.5 text-[15px] flex items-center justify-center gap-2"
            >
              {generateMutation.isPending ? (
                <><Loader2 className="size-4 animate-spin" />Extending...</>
              ) : (
                <>Extend Video <ArrowRight className="size-4" /></>
              )}
            </button>
          )}
          <p className="flex items-center justify-center gap-1 text-[10px] text-[#241d3a]/45 font-bold mt-1.5">
            <IconCoin className="size-2.5" />{creditCost} tokens
          </p>
        </div>
      </Show>
    </>
  );

  return (
    <div className="max-w-xl mx-auto px-5 md:px-8 pt-6 pb-16">
      {/* Tabs */}
      <div className="flex items-center gap-1 mb-6 border-b border-[#241d3a]/10 pb-3">
        {TABS.map((tab) => (
          <Link
            key={tab.href}
            to={tab.href}
            className={cn(
              "px-3.5 py-1.5 rounded-lg text-[13px] font-semibold transition-all",
              tab.href === "/extend-cartoon"
                ? "bg-[#241d3a]/[0.06] text-[#7c5cf0]"
                : "text-[#241d3a]/60 hover:text-[#241d3a] hover:bg-[#241d3a]/[0.04]",
            )}
          >
            {tab.label}
          </Link>
        ))}
      </div>

      <div className="mb-6">
        <h1 className="text-2xl md:text-3xl font-black text-[#241d3a] tracking-tight mb-1">EXTEND VIDEO</h1>
        <p className="text-[14px] text-[#241d3a]/60 font-medium">
          Continue a video from where it left off. Select one of your videos and describe what happens next.
        </p>
      </div>

      {/* Purchase gate */}
      {!hasPurchased ? (
        <div className="flex flex-col items-center justify-center py-16">
          <div className="size-16 rounded-3xl bg-[#b8a5f0]/10 border border-[#b8a5f0]/20 flex items-center justify-center mb-4">
            <Lock className="size-7 text-[#7c5cf0]" strokeWidth={1.5} />
          </div>
          <h3 className="text-base font-bold text-[#241d3a] mb-1.5">Unlock Extend Video</h3>
          <p className="text-sm text-[#241d3a]/60 mb-6 text-center max-w-xs">
            Purchase any token pack to extend your videos. You'll also get watermark-free downloads.
          </p>
          <Link
            to="/billing"
            className="btn-accent text-sm px-6 py-2.5 rounded-xl flex items-center gap-2"
          >
            <IconCoin className="size-4" />
            Get Tokens
          </Link>
        </div>
      ) : selectedVideo && lastFrame ? (
        <div className="space-y-4 animate-fade-in-up">
          <button
            onClick={handleBack}
            className="inline-flex items-center gap-1.5 text-[13px] text-[#241d3a]/60 hover:text-[#241d3a] transition-colors mb-2"
          >
            <ArrowLeft className="size-3.5" />
            Choose different video
          </button>

          <div className="rounded-2xl overflow-hidden border border-[#241d3a]/10 bg-[#f3f0fb]">
            <img
              src={lastFrame}
              alt="Last frame"
              className="w-full max-h-[300px] object-contain"
            />
          </div>

          <div className="rounded-xl bg-[#241d3a]/[0.04] border border-[#241d3a]/10 px-3.5 py-2.5">
            <p className="text-[10px] font-semibold text-[#241d3a]/45 uppercase tracking-wider mb-1">
              Original Prompt
            </p>
            <p className="text-[12px] text-[#241d3a]/60 leading-relaxed">
              {selectedVideo.prompt}
            </p>
          </div>

          <textarea
            value={prompt}
            onChange={(e) => setPrompt(e.target.value)}
            placeholder="Describe what happens next... e.g. character turns around, dramatic wind blows, camera zooms out to reveal the city..."
            rows={3}
            className="input-field resize-none"
          />

          <div className="flex items-center justify-between px-1">
            <div className="flex items-center gap-2 text-[11px] text-[#241d3a]/60">
              <FastForward className="size-3.5" />
              <span>Same model as original</span>
            </div>
            <div className="flex items-center gap-1 text-[11px] text-[#241d3a]/60">
              <IconCoin className="size-3" />
              <span>{creditCost} tokens</span>
            </div>
          </div>

          <div className="hidden md:block">
            {renderExtendButtons()}
          </div>

          <div className="h-20 md:hidden" aria-hidden="true" />

          {createPortal(
            <div className="md:hidden fixed bottom-0 inset-x-0 z-50">
              <div className="px-5 pt-3 pb-3 bg-[#f3f0fb]/95 backdrop-blur-xl border-t border-[#241d3a]/10">
                {renderExtendButtons()}
              </div>
              <div className="bg-[#f3f0fb]/95 backdrop-blur-xl" style={{ height: "env(safe-area-inset-bottom, 0px)" }} />
            </div>,
            document.body,
          )}

          {generateMutation.isError && (
            <NsfwOrError error={generateMutation.error} />
          )}
        </div>
      ) : (
        <div className="animate-fade-in-up">
          {extracting && (
            <div className="flex flex-col items-center justify-center py-16">
              <Loader2 className="size-6 animate-spin text-[#7c5cf0] mb-3" />
              <p className="text-sm text-[#241d3a]/60">Extracting last frame...</p>
            </div>
          )}

          {extractError && (
            <div className="rounded-xl bg-red-400/10 border border-red-400/20 p-3 mb-4">
              <p className="text-[12px] text-red-400">{extractError}</p>
            </div>
          )}

          {!extracting && videosLoading && (
            <div className="flex items-center justify-center py-20">
              <Loader2 className="size-6 animate-spin text-[#7c5cf0]" />
            </div>
          )}

          {!extracting && !videosLoading && completedVideos.length === 0 && (
            <div className="flex flex-col items-center justify-center py-16">
              <div className="size-16 rounded-3xl bg-[#241d3a]/[0.04] border border-[#241d3a]/10 flex items-center justify-center mb-4">
                <Film className="size-7 text-[#7c5cf0]" strokeWidth={1.5} />
              </div>
              <h3 className="text-base font-bold text-[#241d3a] mb-1.5">No videos to extend</h3>
              <p className="text-sm text-[#241d3a]/60 mb-6 text-center max-w-xs">
                Generate a video first, then come back here to extend it.
              </p>
              <Link to="/create-cartoon" className="btn-accent text-sm px-6 py-2.5 rounded-xl">
                Generate Video
              </Link>
            </div>
          )}

          {!extracting && completedVideos.length > 0 && (
            <>
              <p className="text-[13px] font-semibold text-[#241d3a]/60 mb-3">
                Choose a video to extend
              </p>
              <div className="grid grid-cols-2 gap-2.5">
                {completedVideos.map((video) => (
                  <button
                    key={video.id}
                    onClick={() => handleSelectVideo(video)}
                    className="group rounded-2xl border border-[#241d3a]/10 bg-[#241d3a]/[0.04] overflow-hidden hover:border-[#b8a5f0]/30 transition-all cursor-pointer text-left"
                  >
                    <div className="relative w-full aspect-square bg-[#f3f0fb] overflow-hidden">
                      <video
                        src={`${video.url!}#t=0.1`}
                        className="w-full h-full object-contain"
                        muted
                        loop
                        playsInline
                        preload="metadata"
                        onPointerEnter={(e) =>
                          e.currentTarget.play().catch(() => {})
                        }
                        onPointerLeave={(e) => {
                          e.currentTarget.pause();
                          e.currentTarget.currentTime = 0;
                        }}
                      />
                      <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity bg-[#b8a5f0]/20">
                        <div className="size-10 rounded-full bg-[#b8a5f0]/20 backdrop-blur-sm border border-[#b8a5f0]/30 flex items-center justify-center">
                          <FastForward className="size-4 text-[#7c5cf0]" />
                        </div>
                      </div>
                    </div>
                    <div className="px-2.5 py-2">
                      <p className="text-[10px] sm:text-[11px] text-[#241d3a]/60 truncate mb-1">
                        {video.prompt}
                      </p>
                      <div className="flex items-center justify-between">
                        <span className="text-[9px] text-[#241d3a]/45 inline-flex items-center gap-0.5">
                          <IconCoin className="size-2" />{video.credits_used}
                        </span>
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            </>
          )}
        </div>
      )}

      <AuthModal isOpen={showAuth} onClose={() => setShowAuth(false)} title="Sign Up to Extend Videos" />

      <UpsellModal
        isOpen={showCreditsModal}
        onClose={() => setShowCreditsModal(false)}
        creditCost={creditCost}
        credits={credits}
      />
    </div>
  );
}
